import CompanyService from "../../../../app/data/company/companyService";
import UserProfile from "../../../../app/data/user/UserProfile";
import { isXgsiEmailDomain } from "./bolShipperEmailUtils";

const companyService = CompanyService.getInstance();

export interface ResolvedRequesterTeam {
  teamId: string;
  teamName: string;
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : null;

const readString = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
};

/** Extract a display name from team/user API payloads that use varying property names. */
export const extractTeamName = (payload: unknown): string | null => {
  const record = asRecord(payload);
  if (!record) return null;

  const directCandidates = [record.name, record.teamName, record.customerName];
  for (const candidate of directCandidates) {
    const value = readString(candidate);
    if (value) return value;
  }

  const nestedObjects = [record.team, record.customer, record.customerTeam, record.property];
  for (const nested of nestedObjects) {
    const nestedRecord = asRecord(nested);
    if (!nestedRecord) continue;

    for (const candidate of [nestedRecord.name, nestedRecord.teamName]) {
      const value = readString(candidate);
      if (value) return value;
    }
  }

  return null;
};

const extractTeamUsersList = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload;

  const record = asRecord(payload);
  if (!record) return [];

  if (Array.isArray(record.content)) return record.content;
  if (Array.isArray(record.users)) return record.users;

  return [];
};

export const resolveRequesterTeamName = async (teamId: string): Promise<string | null> => {
  const response = await companyService.getTeamUsers(teamId, true);
  if (!response.ok()) return null;

  const fromPayload = extractTeamName(response.data);
  if (fromPayload) return fromPayload;

  const users = extractTeamUsersList(response.data);
  for (const user of users) {
    const teamName = extractTeamName(user);
    if (teamName) return teamName;
  }

  return null;
};

/** Pull a bare email from RFC5322-style values like `Name <user@example.com>`. */
export const extractEmailAddress = (value?: string): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  const bracketed = trimmed.match(/<([^>]+@[^>]+)>/);
  if (bracketed?.[1]) return bracketed[1].trim();
  const plain = trimmed.match(/[^\s<>]+@[^\s<>]+\.[^\s<>]+/);
  if (plain?.[0]) return plain[0].trim();
  return trimmed.includes("@") ? trimmed : undefined;
};

export const isEmailRegisteredOnTeam = async (
  teamId: string,
  email: string
): Promise<boolean> => {
  const normalized = extractEmailAddress(email)?.toLowerCase();
  if (!normalized) return false;

  const response = await companyService.getTeamUsers(teamId, true);
  if (!response.ok()) return false;

  const users = extractTeamUsersList(response.data);
  if (!Array.isArray(users)) return false;

  return users.some((user: { email?: string; pendingEmail?: string; status?: string }) => {
    const registered = extractEmailAddress(user.email)?.toLowerCase();
    const pending = extractEmailAddress(user.pendingEmail)?.toLowerCase();
    return registered === normalized || pending === normalized;
  });
};

export const getEffectiveSenderEmail = (
  senderEmail?: string,
  forwardedSenderEmail?: string,
  fallbackSender?: string
): string | undefined => {
  const candidates = [
    extractEmailAddress(senderEmail),
    extractEmailAddress(forwardedSenderEmail),
    extractEmailAddress(fallbackSender),
  ].filter((email): email is string => !!email);

  const externalSender = candidates.find((email) => !isXgsiEmailDomain(email.toLowerCase()));
  return externalSender ?? candidates[0];
};

const matchesEmailAddress = (candidate: string | undefined, target: string): boolean => {
  const normalized = extractEmailAddress(candidate)?.toLowerCase();
  return !!normalized && normalized === target;
};

const resolveTeamFromCustomerUsers = async (
  email: string
): Promise<ResolvedRequesterTeam | null> => {
  const response = await companyService.getAllUsers(undefined, {
    search: email,
    company: "",
    role: null,
    status: null,
  });
  if (!response.ok()) return null;

  const users: UserProfile[] = response.data?.content ?? [];
  if (!Array.isArray(users) || users.length === 0) return null;

  const matchedUser =
    users.find(
      (user) =>
        matchesEmailAddress(user.email, email) || matchesEmailAddress(user.pendingEmail, email)
    ) ?? (users.length === 1 ? users[0] : undefined);

  const teamId =
    matchedUser?.team?.id ??
    (asRecord(matchedUser)?.teamId as string | undefined) ??
    readString(asRecord(matchedUser)?.customerTeamId);

  if (!teamId) return null;

  const teamName = extractTeamName(matchedUser) ?? (await resolveRequesterTeamName(teamId));
  if (!teamName) return null;

  return { teamId, teamName };
};

/** When requester company is unset, resolve team id from sender email, then load team name by team id. */
export const resolveRequesterTeamFromSenderEmail = async (
  senderEmail?: string
): Promise<ResolvedRequesterTeam | null> => {
  const email = extractEmailAddress(senderEmail)?.toLowerCase();
  if (!email || isXgsiEmailDomain(email)) return null;

  return resolveTeamFromCustomerUsers(email);
};
